// src/components/padrekko/qc/correctionStages.js
//
// Which review stages have corrected a finding — the fold behind the
// [D][C][O] CORRECTED badge (CorrectionStagePill in QcFindingCard).
//
// Lives in its own module rather than inside the card because exporting a
// non-component from a component file breaks React fast refresh, and it lets
// the fold be tested without mounting the card.
//
// Keyed on the history entry's by_role (WHO corrected), not case_stage
// (WHERE the case sat). The two disagree on the kickback path: DH reworks a
// finding while the case is still parked at customer_review (see
// canWriteFindingWithKickback in requireCaseAccess.js). by_role is what a
// reviewer means by "DH corrected it".
//
// Only 'reanalyzed' counts. It's the sole event that rewrites analysis, and
// the sole one that sets operator_trained — so the chips can never disagree
// with the pill's own trigger. A 'polygon_edited' entry is always appended
// alongside a 'reanalyzed' one by the same request (padrekko-qc.mjs composes
// both), so counting it too would double-count, not widen coverage.

const CORRECTION_STAGE_LETTER = {
  dh_analyst:       "D",
  customer_analyst: "C",
  operator_analyst: "O",
};

// Fixed pipeline order, NOT chronological — the badge reads as a stage
// ladder filling left to right. Identical to chronological in the happy
// path; differs only when DH re-corrects after a customer kickback, which
// renders [D][C] rather than [C][D].
const CORRECTION_STAGE_SEQUENCE = ["D", "C", "O"];

const CORRECTION_STAGE_LABEL = {
  D: "DreamHammer",
  C: "Customer",
  O: "Operator",
};

// history → [{ letter, label, at, count }] in D→C→O order. `at` is the
// FIRST correction by that stage (when it entered the chain); `count` folds
// repeat corrections by the same stage into the one chip. Returns [] for
// missing/empty history or for roles outside the three-stage pipeline —
// callers render the outer badge without chips in that case.
export function correctionStages(history) {
  if (!Array.isArray(history)) return [];
  const seen = new Map();
  for (const h of history) {
    if (h?.event !== "reanalyzed") continue;
    const letter = CORRECTION_STAGE_LETTER[h?.by_role];
    if (!letter) continue;
    const prior = seen.get(letter);
    if (prior) {
      prior.count += 1;
      continue;
    }
    seen.set(letter, {
      letter,
      label: CORRECTION_STAGE_LABEL[letter],
      at: h?.at ?? null,
      count: 1,
    });
  }
  return CORRECTION_STAGE_SEQUENCE.filter((l) => seen.has(l)).map((l) => seen.get(l));
}
